import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useAllFeideOrganizations,
  useMySystemRoles,
} from "../../../api/hooks";
import { IconCheckMark } from "../../../common/Icons/Icons";
import styles from "./SuperadminFeideOrganizations.module.css";

const SuperadminFeideOrganizations = () => {
  const { t } = useTranslation("common");

  // React Query hooks
  const { data: systemRolesData } = useMySystemRoles();
  const { data: orgsData, isLoading } = useAllFeideOrganizations();

  const systemRoles = systemRolesData?.data || [];
  const isSystemAdmin = systemRoles.includes("systemadmin");
  const organizations = orgsData?.data || [];

  // Extract Norwegian org number from feideId
  // Format: fc:org:testusers.feide.no:unit:NO856326499
  const extractOrgNumber = (feideId) => {
    if (!feideId) return null;
    const match = feideId.match(/NO(\d+)/);
    return match ? match[1] : null;
  };

  const enrichedOrganizations = useMemo(() => {
    return organizations.map(org => ({
      ...org,
      norwegianOrgId: extractOrgNumber(org.feideId),
    }));
  }, [organizations]);

  // If not superadmin, don't render anything
  if (!isSystemAdmin) {
    return null;
  }

  if (isLoading) {
    return <div className={styles.loading}>{t("auth.settings.organizations.loading")}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>{t("auth.settings.feideOrgs.title")}</h2>
        <p className={styles.description}>
          {t("auth.settings.feideOrgs.description")}
        </p>
      </div>

      {enrichedOrganizations.length === 0 ? (
        <div className={styles.empty}>
          <p>{t("auth.settings.feideOrgs.noOrganizations")}</p>
          <p className={styles.hint}>
            {t("auth.settings.feideOrgs.hint")}
          </p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t("auth.settings.feideOrgs.table.name")}</th>
                <th>{t("auth.settings.feideOrgs.table.orgNumber")}</th>
                <th>{t("auth.settings.feideOrgs.table.feideId")}</th>
                <th>{t("auth.settings.feideOrgs.table.type")}</th>
                <th>{t("auth.settings.feideOrgs.table.status")}</th>
              </tr>
            </thead>
            <tbody>
              {enrichedOrganizations.map((org) => {
                const isImported = org.mappings && org.mappings.length > 0;
                const digupOrg = isImported ? org.mappings[0].digupGroup : null;

                return (
                  <tr key={org.id}>
                    <td className={styles.orgName}>{org.name}</td>
                    <td className={styles.orgNumber}>
                      {org.norwegianOrgId || '-'}
                    </td>
                    <td className={styles.feideId}>{org.feideId}</td>
                    <td className={styles.type}>{org.orgType || org.type}</td>
                    <td className={styles.status}>
                      {isImported ? (
                        <span className={styles.imported}>
                          <IconCheckMark />
                          {t("auth.settings.feideOrgs.imported")}
                          {digupOrg && (
                            <span className={styles.digupName}>
                              {" "}
                              (ID: {digupOrg.id})
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className={styles.notImported}>{t("auth.settings.feideOrgs.notImported")}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SuperadminFeideOrganizations;
